// Bank registry: where each bank's card alerts come from and how to switch them on.
//
// Only mark an entry `verified` when its sender and subject come from a real alert. A Gmail
// filter is built only from banks that have at least one exact sender address and a subject
// pattern; never from a whole domain.

export interface BankEntry {
  id: string;
  name: string;
  /** Exact alert sender addresses (lowercase), from real alerts. Empty when not known yet. */
  senders: string[];
  /**
   * Addresses this bank may send alerts from, not yet seen on a real purchase alert. Never used
   * in a filter; listed so nobody can save one as their own Gmail address.
   */
  candidateSenders?: string[];
  /** Gmail subject query (goes in Gmail's Subject box), e.g. transaction. Empty when not known yet. */
  subjectPattern: string;
  /** Short steps for switching on email alerts for every purchase. */
  steps: string[];
  /** True only when senders and subject come from a real alert. Only verified banks can be ticked. */
  verified: boolean;
  /**
   * True only when the steps' menu names were checked on the bank's real site or app. Until then
   * the add-a-card flow shows GENERIC_BANK_STEPS with the bank's name, and says so.
   */
  stepsVerified: boolean;
}

/** Steps for a bank Hush learned from other households (registry promotion). */
export const GENERIC_BANK_STEPS = [
  "Sign in to your bank on a computer and open your card's alert settings.",
  'Turn on the purchase alert. Set the lowest amount allowed so every purchase sends one.',
  'Choose email, use your Gmail address, and save.',
];

export const BANKS: BankEntry[] = [
  {
    id: 'chase',
    name: 'Chase',
    // From real alerts: "You made a $72.56 transaction" and "You made a $72.56 transaction with
    // Amazon.com". "Your credit card payment is scheduled" comes from the same address and must
    // not match, which the word "transaction" alone takes care of.
    senders: ['no.reply.alerts@chase.com'],
    subjectPattern: 'transaction',
    steps: [
      "Sign in at chase.com on a computer and open your card's alert settings.",
      'Turn on the purchase alert.',
      'Set the amount to $0.00 so every purchase sends one.',
      'Choose email, use your Gmail address, and save.',
    ],
    verified: true,
    // The alert itself is verified; these menu names are not.
    stepsVerified: false,
  },
  {
    id: 'capital-one',
    name: 'Capital One',
    // Not verified: no real purchase alert seen yet. This address also sends fraud checks, so a
    // subject match will be needed too. Until then Capital One goes through "My bank is not listed".
    senders: [],
    candidateSenders: ['capitalone@notification.capitalone.com'],
    subjectPattern: '',
    steps: GENERIC_BANK_STEPS,
    verified: false,
    stepsVerified: false,
  },
];

export const bankById = (id: string) => BANKS.find((b) => b.id === id);

/** The registry entry whose verified senders include this address (an example alert's sender). */
export const bankBySender = (sender: string) => BANKS.find((b) => b.senders.some((s) => s.toLowerCase() === sender.trim().toLowerCase()));

/** How to turn on alerts at a bank: its own steps only when their menu names are verified. */
export const alertSteps = (b: BankEntry | undefined): { steps: string[]; verified: boolean } =>
  b?.stepsVerified ? { steps: b.steps, verified: true } : { steps: GENERIC_BANK_STEPS, verified: false };

/** A single, exact email address: no wildcard, no bare domain. */
export const EXACT_SENDER = /^[a-z0-9._%+-]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/;

/** One bank's part of the filter: its exact senders and its subject pattern. */
export interface FilterBank {
  name: string;
  senders: string[];
  subjectPattern: string;
}

export interface GmailFilter {
  /** For Gmail's "From" box. */
  from: string;
  /** For Gmail's "Subject" box. Set for one bank; empty for several (see hasWords). */
  subject: string;
  /** For Gmail's "Has the words" box. Empty for one bank; for several, each bank's subject paired with its own senders. */
  hasWords: string;
  /** Best effort: opens Gmail's new filter dialog prefilled. May not work. */
  createLink: string;
  /** Banks in the filter. */
  included: string[];
  /** Banks left out, and why. */
  missing: { name: string; reason: 'no-sender' | 'no-subject' }[];
}

/** Lowercased exact senders only; anything else is dropped. */
export const exactSenders = (senders: string[]) => [...new Set(senders.map((s) => s.trim().toLowerCase()).filter((s) => EXACT_SENDER.test(s)))];

const group = (senders: string[]) => (senders.length === 1 ? senders[0] : `(${senders.join(' OR ')})`);

/**
 * The Gmail filter for these banks, or null when none has both an exact sender and a subject
 * pattern. Never a whole domain, never a sender without its subject.
 *
 * One bank:      From: no.reply.alerts@chase.com   Subject: transaction
 * Several banks: From: a@chase.example OR b@bank.example
 *                Has the words: (from:a@chase.example subject:(transaction)) OR (from:b@bank.example subject:(purchase))
 * Grouped OR keeps each bank's subject paired with its own senders.
 */
export function buildGmailFilter(banks: FilterBank[]): GmailFilter | null {
  const missing: GmailFilter['missing'] = [];
  const usable: FilterBank[] = [];
  for (const b of banks) {
    const senders = exactSenders(b.senders);
    const subjectPattern = b.subjectPattern.trim();
    if (!senders.length) missing.push({ name: b.name, reason: 'no-sender' });
    else if (!subjectPattern) missing.push({ name: b.name, reason: 'no-subject' });
    else if (!usable.some((u) => u.senders.join() === senders.join() && u.subjectPattern === subjectPattern)) usable.push({ name: b.name, senders, subjectPattern });
  }
  if (!usable.length) return null;
  const from = [...new Set(usable.flatMap((b) => b.senders))].join(' OR ');
  const single = usable.length === 1;
  const subject = single ? usable[0].subjectPattern : '';
  const hasWords = single ? '' : usable.map((b) => `(from:${group(b.senders)} subject:(${b.subjectPattern}))`).join(' OR ');
  const params = [`from=${encodeURIComponent(from)}`, subject && `subject=${encodeURIComponent(subject)}`, hasWords && `has=${encodeURIComponent(hasWords)}`].filter(Boolean);
  const createLink = `https://mail.google.com/mail/u/0/#create-filter/${params.join('&')}`;
  return { from, subject, hasWords, createLink, included: usable.map((b) => b.name), missing };
}

/** Every address a bank in the registry sends from, or may send from. Never anyone's own address. */
export const registrySenders = () => new Set(BANKS.flatMap((b) => [...b.senders, ...(b.candidateSenders ?? [])]).map((s) => s.toLowerCase()));
